import React, { memo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";

import { useStore } from "src/store";
import { Icon, Spinner } from "src/primitives";
import api, { useAsync } from "src/api";
import { getClass, useEventCb, getProviderLogo, getSearchPlaceholder } from "src/utils";
import AutoComplete from "../AutoComplete";
import styles from "./style.css";
import {useTranslation} from "react-i18next";

const Input = memo(props => {
  const {
    provider,
    sticky,
    loading,
    query,
    setQuery,
    submitQuery,
    autocomplete,
    enhancedByGoogleLogoVisibility = false,
    enhancedByGoogleLogoStyle = 3,
    ...rest
  } = props;
  const logo = getProviderLogo(provider);

  return (
    <div class={getClass(styles.inputContainer, enhancedByGoogleLogoStyle === 3 && styles.inputContainerStyle3)}>
      {!sticky &&
        enhancedByGoogleLogoStyle !== 1 &&
        (provider === "googlecse" || provider === "googlecse-inline") &&
        enhancedByGoogleLogoVisibility !== false && <img src={`${window.GLOBALS.ASSETS.GENERIC}/img/search/enhanced_by_google.png`} />}
      <div class={getClass(styles.input, sticky && styles.sticky)}>
        <img src={`${window.GLOBALS.ASSETS.GENERIC}/img/${logo}`} style={props.searchLogo && logo ? "" : "display:none"} />
        <input {...rest} type="search" autoFocus value={query} onChange={e => setQuery(e.target.value)} />
        {sticky ? <button type="submit">{loading ? <Spinner size={0.6} /> : <Icon size={0.75} name="search" />}</button> : null}
      </div>
      {!sticky &&
        enhancedByGoogleLogoStyle === 1 &&
        (provider === "googlecse" || provider === "googlecse-inline") &&
        enhancedByGoogleLogoVisibility !== false && <img src={`${window.GLOBALS.ASSETS.GENERIC}/img/search/enhanced_by_google.png`} />}
      {autocomplete ? <AutoComplete query={query} setQuery={q => submitQuery(null, q)} /> : null}
    </div>
  );
});

const SearchBar = memo(props => {
  const {
    searchTemplateSettings = {},
    searchProvider = "",
    searchProviderOverride = "",
    searchBarPlaceholderText = "Search the web",
    searchLabel = "Search",
    history,
    enhancedByGoogleLogoVisibility = true,
    enhancedByGoogleLogoStyle,
    autocomplete: autocompleteProp = false,
    bingTrendingTerms = 0,
    searchLogo = true
  } = props;

  const provider = searchProviderOverride || searchProvider;
  const isSERP = provider === "bing" || provider.startsWith("google") || provider === "faroo";
  const autocomplete = provider === "bing" && bingTrendingTerms ? false : autocompleteProp;

  const store = useStore("config");
  const { location = {}, clientid: clientId, brand } = store("config");
  const { lat, lng } = location;
  const {t: translator} = useTranslation();
  const {
    hideLogo,
    logo = `${window.GLOBALS.ASSETS.ROOT}/ext/50012/1627665338105-search-logo.png`
  } = searchTemplateSettings;

  const searchParams = { lat, lng, clientId, brand, provider };

  const useSearch = useAsync(api.getSearch, { callOnMount: false });
  const search = useSearch();

  const container = useRef();
  const [query, setQuery] = useState("");
  const [sticky, setSticky] = useState(false);

  const checkSticky = () => {
    const { current } = container;
    const { parentElement } = current || {};
    setSticky(parentElement && parentElement.classList.contains("sticky-component"));
  };

  useEventCb("scroll", checkSticky);

  if (search.data.redirectURL) {
    window.location.href = search.data.redirectURL;
  }

  const handleSubmit = (e, q = query) => {
    e && e.preventDefault();
    if (isSERP) {
      history.push(`/_search?q=${q}&provider=${provider}`); // Open SERP page & search
    } else {
      search.call({ ...searchParams, searchTerm: q }); // Search manually (waiting for a redirectURL)
    }
  };

  const placeholder = getSearchPlaceholder(searchBarPlaceholderText, translator);

  return (
    <form ref={container} class={getClass(styles.container, sticky && styles.sticky)} onSubmit={handleSubmit}>
      {!hideLogo && <img src={logo} />}
      <Input
        placeholder={placeholder}
        loading={search.loading}
        sticky={sticky}
        provider={provider}
        query={query}
        setQuery={setQuery}
        submitQuery={handleSubmit}
        autocomplete={autocomplete}
        enhancedByGoogleLogoVisibility={enhancedByGoogleLogoVisibility}
        enhancedByGoogleLogoStyle={enhancedByGoogleLogoStyle}
        searchLogo={searchLogo}
      />
      <button type="submit" class={styles.button}>
        {search.loading ? <Spinner size={0.7} /> : translator(`Common.${searchLabel}`)}
      </button>
    </form>
  );
});

export default withRouter(SearchBar);
